import moment from 'moment';
import { DATE_FORMAT } from '../components/common/Constants/Constants';

const DAYS_IN_GRID = 42;

export const toValidDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = value instanceof Date ? moment(value) : moment(value, [DATE_FORMAT, moment.ISO_8601], true);
  const fallback = parsed.isValid() ? parsed : moment(new Date(value));
  return fallback.isValid() ? fallback.startOf('day').toDate() : null;
};

export const parseTypedDate = (text) => {
  const parsed = moment(text, DATE_FORMAT, true);
  return parsed.isValid() ? parsed.toDate() : null;
};

export const formatDate = (date) => (date ? moment(date).format(DATE_FORMAT) : '');

export const getToday = () => moment().startOf('day').toDate();

const yearsFromNow = (yearLimit, direction) =>
  yearLimit
    ? moment()
        .add(direction * yearLimit, 'years')
        .toDate()
    : null;

export const resolveDateLimits = ({ minDate, initDate, maxDate, yearLimit }) => ({
  min: toValidDate(minDate || initDate) || yearsFromNow(yearLimit, -1),
  max: toValidDate(maxDate) || yearsFromNow(yearLimit, 1)
});

export const isSameDay = (firstDate, secondDate) => Boolean(firstDate && secondDate && moment(firstDate).isSame(secondDate, 'day'));

export const isSameYear = (year, date) => Boolean(date) && year === date.getFullYear();

export const isSameMonth = (year, month, date) => isSameYear(year, date) && month === date.getMonth();

export const isOutOfRange = (date, minDate, maxDate) =>
  Boolean((minDate && moment(date).isBefore(minDate, 'day')) || (maxDate && moment(date).isAfter(maxDate, 'day')));

export const buildMonthGrid = (viewDate) => {
  const month = moment(viewDate).month();
  const start = moment(viewDate).startOf('month').startOf('week');
  return Array.from({ length: DAYS_IN_GRID }, (_, index) => {
    const day = start.clone().add(index, 'days');
    return { date: day.toDate(), inMonth: day.month() === month };
  });
};

export const getMonthNames = (locale, style = 'long') => {
  const formatter = new Intl.DateTimeFormat(locale, { month: style });
  return Array.from({ length: 12 }, (_, month) => formatter.format(new Date(2000, month, 1)).replace('.', ''));
};

export const getWeekdayNames = (locale) => {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  return Array.from({ length: 7 }, (_, day) => formatter.format(new Date(2000, 0, 2 + day)).slice(0, 2));
};

export const YEARS_PER_PAGE = 12;

export const getYearPageStart = (year) => year - (((year % YEARS_PER_PAGE) + YEARS_PER_PAGE) % YEARS_PER_PAGE);

export const getYearPageLabel = (pageStart) => `${pageStart} – ${pageStart + YEARS_PER_PAGE - 1}`;

export const getYearPage = (start) => Array.from({ length: YEARS_PER_PAGE }, (_, index) => start + index);

export const isYearOutOfRange = (year, minDate, maxDate) =>
  Boolean((minDate && year < minDate.getFullYear()) || (maxDate && year > maxDate.getFullYear()));

export const isMonthOutOfRange = (year, month, minDate, maxDate) => {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  return Boolean((minDate && moment(last).isBefore(minDate, 'day')) || (maxDate && moment(first).isAfter(maxDate, 'day')));
};

export const getTodayLabel = (locale) => {
  const word = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(0, 'day');
  return word.charAt(0).toUpperCase() + word.slice(1);
};

export const buildCellClass = (base, { isToday = false, isSelected = false, extra = [] } = {}) =>
  [base, ...extra, isToday ? 'is-today' : '', isSelected ? 'is-selected' : ''].filter(Boolean).join(' ');
